import React, { useState, useEffect } from 'react';
import { getTickets, createTicket, getStudios } from '@/lib/api';
import { MessageSquare, Loader2, Plus, Send, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const statusColors = {
  open: 'bg-[#F5E6E6] text-[#8A3D3D]',
  in_progress: 'bg-[#F4EFE6] text-[#7A6849]',
  resolved: 'bg-[#E8EDE5] text-[#3A4035]',
  closed: 'bg-[#F2F0EB] text-[#5C5C55]',
};

export default function ReportIssue() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ studio_id: '', category: '', subject: '', description: '', priority: 'medium' });

  useEffect(() => {
    getTickets().then(setTickets).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const newTicket = await createTicket(form);
      setTickets(prev => [newTicket, ...prev]);
      setShowForm(false);
      setForm({ studio_id: '', category: '', subject: '', description: '', priority: 'medium' });
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="report-issue-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Support</h1>
          <p className="text-sm text-[#5C5C55] mt-1">Report issues and track requests</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}
            className="bg-[#3A4035] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2B3027]"
            data-testid="new-ticket-button">
            <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Report Issue
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6 mb-6" data-testid="ticket-form">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A]">Report an Issue</h2>
            <button onClick={() => setShowForm(false)} className="text-xs text-[#8A8B85] hover:text-[#1A1A1A]">Cancel</button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="ticket-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {['Plumbing', 'Electrical', 'Heating', 'Cleaning', 'Security', 'General', 'Other'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Subject</Label>
              <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief description of the issue" required
                className="bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="ticket-subject" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Provide as much detail as possible" required rows={4}
                className="bg-[#F2F0EB] border-transparent rounded-xl placeholder:text-[#8A8B85] resize-none" data-testid="ticket-description" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger className="bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="ticket-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={submitting || !form.category || !form.subject || !form.description}
              className="w-full bg-[#3A4035] text-white rounded-full h-11 font-medium hover:bg-[#2B3027]"
              data-testid="submit-ticket-button">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />}
              Submit Report
            </Button>
          </form>
        </div>
      )}

      {/* Existing tickets */}
      <div className="space-y-3">
        {tickets.map((tkt, i) => (
          <div key={tkt.ticket_id}
            className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-5 animate-fade-in stagger-${(i % 6) + 1}`}
            data-testid={`my-ticket-${tkt.ticket_id}`}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#F2F0EB] flex items-center justify-center shrink-0 mt-0.5">
                <MessageSquare className="w-4 h-4 text-[#5C5C55]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-medium text-[#1A1A1A]">{tkt.subject}</h3>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize shrink-0 ${statusColors[tkt.status] || ''}`}>
                    {tkt.status?.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-xs text-[#8A8B85] mt-0.5">{tkt.category} · {tkt.priority} priority</p>
                <p className="text-sm text-[#5C5C55] mt-2">{tkt.description}</p>
                {tkt.notes && (
                  <div className="mt-3 bg-[#F2F0EB] rounded-xl p-3">
                    <p className="text-xs font-semibold text-[#5C5C55] mb-1">Response</p>
                    <p className="text-sm text-[#5C5C55]">{tkt.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {tickets.length === 0 && !showForm && (
        <div className="text-center py-16">
          <MessageSquare className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#8A8B85]">No support tickets. Everything running smoothly!</p>
        </div>
      )}
    </div>
  );
}
